import * as pc from 'playcanvas';

/**
 * Loads CC0 photo-PBR texture sets (Poly Haven + ambientCG — see
 * public/textures/LICENSE.txt) for every surface the game controls: ground,
 * plazas, roads, sidewalks and photo-facade buildings. Textures load once per
 * set and are shared across every material built from them; each usage clones
 * with its own tiling so a differently-sized surface still reads at a
 * consistent real-world scale.
 */

export interface PbrTextureUrls {
  diffuse: string;
  normal: string;
  rough: string;
  /** Not every ambientCG set ships an AO map. */
  ao?: string;
}

interface PbrTextures {
  diffuse: pc.Texture | null;
  normal: pc.Texture | null;
  ao: pc.Texture | null;
  rough: pc.Texture | null;
}

const textureCache = new Map<string, Promise<PbrTextures>>();

function loadTexture(app: pc.Application, url: string): Promise<pc.Texture | null> {
  return new Promise((resolve) => {
    const asset = new pc.Asset(url, 'texture', { url });
    asset.once('load', () => resolve(asset.resource as pc.Texture));
    asset.once('error', (err: unknown) => {
      console.warn(`[GOC materials] failed to load ${url}:`, err);
      resolve(null);
    });
    app.assets.add(asset);
    app.assets.load(asset);
  });
}

export function loadPbrTextures(app: pc.Application, urls: PbrTextureUrls): Promise<PbrTextures> {
  let pending = textureCache.get(urls.diffuse);
  if (!pending) {
    pending = (async () => ({
      diffuse: await loadTexture(app, urls.diffuse),
      normal: await loadTexture(app, urls.normal),
      ao: urls.ao ? await loadTexture(app, urls.ao) : null,
      rough: await loadTexture(app, urls.rough),
    }))();
    textureCache.set(urls.diffuse, pending);
  }
  return pending;
}

/** Builds a StandardMaterial with independent U/V tiling (facade walls tile
 * window bays horizontally and floors vertically at different rates). An
 * optional tint multiplies the photo — used to darken locked buildings. */
export function pbrMaterialUV(
  textures: PbrTextures,
  tilesU: number,
  tilesV: number,
  tint?: string,
): pc.StandardMaterial {
  const mat = new pc.StandardMaterial();
  const tiling = new pc.Vec2(tilesU, tilesV);

  if (textures.diffuse) {
    mat.diffuseMap = textures.diffuse;
    mat.diffuseMapTiling = tiling;
  }
  if (textures.normal) {
    mat.normalMap = textures.normal;
    mat.normalMapTiling = tiling;
  }
  if (textures.ao) {
    mat.aoMap = textures.ao;
    mat.aoMapTiling = tiling;
  }
  if (textures.rough) {
    mat.glossMap = textures.rough;
    mat.glossInvert = true; // sources ship roughness, not gloss
    mat.gloss = 1;
    mat.glossMapTiling = tiling;
  }
  // Dielectric F0 — without this StandardMaterial's specular is black and glass/
  // wet surfaces render lifeless, with no sky reflection from the env atlas.
  mat.specular.set(0.045, 0.045, 0.045);
  if (tint) {
    mat.diffuse.fromString(tint);
  }
  mat.update();
  return mat;
}

/** Square tiling from a real-world tile size; `worldSize` is the surface's own
 * edge length so `metersPerTile` reads consistently regardless of surface size. */
export function pbrMaterial(
  textures: PbrTextures,
  metersPerTile: number,
  worldSize: number,
  tint?: string,
): pc.StandardMaterial {
  return pbrMaterialUV(textures, worldSize / metersPerTile, worldSize / metersPerTile, tint);
}
